import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FieldArray, Formik } from "formik";
import * as Yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { format } from "date-fns";
import { ArrowLeft, Banknote, Plus, Wallet, X } from "lucide-react";

import { createInvoice, getInvoiceGroups } from "src/api/invoice";
import { getCustomers } from "src/api/customer";
import { getInventory } from "src/api/inventory";
import { useAuth } from "src/providers/AuthProvider";
import { useDashboardContext } from "src/context/DashboardContext";
import ComboSearch from "src/components/ComboSearch";
import CustomModal from "src/components/CustomModal";
import NewCustomerModal from "src/components/modals/NewCustomerModal";
import AddProductModal from "src/components/modals/AddProductModal";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { EmptyState, PageHead } from "src/components/common";
import {
	cn,
	getCustomizationValue,
	removeFromLocalStorage,
	renderCurrency,
	renderNumber,
	showErrorToast,
	showInfoToast,
	showSuccessToast,
} from "src/lib/utils";

interface ProductOption {
	id?: number | string;
	name?: string;
	sku?: string;
	price?: number | string;
	available_qty?: number;
}

interface InvoiceItemValue {
	product_id: string;
	qty: number;
	product: ProductOption;
}

interface CustomerOption {
	id?: number | string;
	first_name?: string;
	last_name?: string;
}

interface InvoiceFormValues {
	customer: CustomerOption | null;
	customer_id: string;
	group_id: string;
	invoice_items: InvoiceItemValue[];
	discount: number | string;
	due_date: string;
	payment_method: string;
}

const initialValues: InvoiceFormValues = {
	customer: null,
	customer_id: "",
	group_id: "",
	invoice_items: [],
	discount: 0,
	due_date: format(new Date(), "yyyy-MM-dd"),
	payment_method: "",
};

const validationSchema = Yup.object()
	.shape({
		customer_id: Yup.string(),
		group_id: Yup.string(),
		invoice_items: Yup.array()
			.of(
				Yup.object().shape({
					product_id: Yup.string().required("Product ID is required"),
					qty: Yup.number().required("Quantity is required").min(1, "Quantity must be at least 1"),
				})
			)
			.min(1, "At least one invoice item is required"),
		discount: Yup.number().min(0, "Discount cannot be negative").max(100, "Discount cannot be more than 100%"),
		due_date: Yup.date().required("Due date is required"),
		payment_method: Yup.string().required("Payment method is required"),
	})
	.test("customer-or-group", "Either customer or group must be selected, not both", function (values) {
		const hasCustomer = Boolean(values.customer_id);
		const hasGroup = Boolean(values.group_id);
		return (hasCustomer || hasGroup) && !(hasCustomer && hasGroup);
	});

const paymentMethods = [
	{ value: "transfer", label: "Transfer", icon: Banknote },
	{ value: "offline", label: "Offline", icon: Wallet },
];

const AddInvoice = () => {
	const { user } = useAuth();
	const navigate = useNavigate();
	const { settings } = useDashboardContext();

	const invoiceLabel = getCustomizationValue(user, "invoice_label", true) || "Invoice";
	const customerLabel = getCustomizationValue(user, "customer_label", true) || "Customer";

	const { mutate, isLoading } = useMutation(createInvoice, {
		onSuccess: ({ message, data }) => {
			showSuccessToast(message || `${invoiceLabel} created`);
			// Group invoices are created asynchronously (queued server-side) and the response
			// has no uuid yet, so there's no invoice to open a detail modal for or initiate
			// payment on — just return to the list, which will show it once the job finishes.
			if (data?.uuid) {
				// Invoice detail is a modal on the invoices page now (decision D2);
				// `initiate=1` still asks it to kick off payment straight away.
				navigate(`/invoices?invoice=${data.uuid}&initiate=1`);
			} else {
				navigate("/invoices");
			}
		},
	});

	useEffect(() => () => removeFromLocalStorage("invoice_items"), []);

	// The BVN gate used to run during render (toast + navigate), which is a side effect
	// in the render phase. Moved into an effect.
	const bvnVerified = Boolean(user?.bvn_verified);
	useEffect(() => {
		if (!bvnVerified) {
			showInfoToast("Please verify your BVN before proceeding");
			navigate("/account");
		}
	}, [bvnVerified, navigate]);

	if (!bvnVerified) return null;

	const vatRate = settings?.is_vat_enabled ? (settings?.vat ?? 0) / 100 : 0;

	return (
		<div className="tw-space-y-5">
			<PageHead
				title={`New ${invoiceLabel}`}
				subtitle={`Bill a ${customerLabel.toLowerCase()} or a group for items from your inventory.`}
				actions={
					<Button size="sm" variant="outline" className="tw-gap-1.5" onClick={() => navigate("/invoices")}>
						<ArrowLeft className="tw-h-4 tw-w-4" />
						Back
					</Button>
				}
			/>

			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={({ invoice_items, ...values }) =>
					mutate({ ...values, invoice_items: invoice_items.map(({ product, ...rest }) => ({ ...rest })) })
				}
			>
				{({ values, handleChange, handleSubmit, setFieldValue, errors, touched }) => {
					const subTotal = values.invoice_items.reduce((sum, item) => sum + Number(item.product.price ?? 0) * item.qty, 0);
					const discountAmount = subTotal * (Number(values.discount) / 100);
					const vatAmount = Math.max((subTotal - discountAmount) * vatRate, 0);
					const grandTotal = subTotal - discountAmount + vatAmount;

					return (
						<form onSubmit={handleSubmit} className="tw-space-y-4">
							{/* Bill to */}
							<section className="tw-rounded-lg tw-border tw-border-border tw-bg-white tw-p-4 tw-shadow-card sm:tw-p-5">
								<h2 className="tw-mb-3 tw-text-[15px] tw-font-bold tw-text-text">Bill to</h2>

								<div className="tw-flex tw-flex-wrap tw-items-end tw-gap-4">
									{values.customer ? (
										<div className="tw-flex tw-flex-1 tw-items-center tw-gap-2">
											<div className="tw-flex tw-h-11 tw-min-w-[240px] tw-flex-1 tw-items-center tw-rounded-[10px] tw-border tw-border-border tw-bg-bg tw-px-3 tw-text-[14px] tw-font-medium tw-text-text">
												{[values.customer.first_name, values.customer.last_name].filter(Boolean).join(" ")}
											</div>
											<Button
												type="button"
												size="sm"
												variant="outline"
												className="tw-text-red"
												onClick={() => {
													setFieldValue("customer", null);
													setFieldValue("customer_id", "");
												}}
											>
												Clear
											</Button>
										</div>
									) : (
										<div className="tw-flex tw-flex-1 tw-flex-wrap tw-items-end tw-gap-3">
											<div className="tw-min-w-[220px] tw-flex-1 tw-space-y-1.5">
												<Label>{customerLabel}</Label>
												<ComboSearch
													value={values.customer_id}
													onChange={(value: string) => {
														setFieldValue("customer_id", value);
														setFieldValue("group_id", "");
													}}
													placeholder={`Search ${customerLabel.toLowerCase()}...`}
													className="tw-h-11 tw-w-full tw-justify-between tw-rounded-[10px] tw-border-border tw-text-[14px]"
													optionRenderer={({ first_name, last_name }: CustomerOption) => (
														<p>
															{first_name} {last_name}
														</p>
													)}
													queryKey="customers"
													queryFN={getCustomers}
													mapFN={(option: CustomerOption) => ({
														label: `${option.first_name} ${option.last_name}`,
														value: String(option.id),
														info: option,
													})}
												/>
											</div>

											<span className="tw-pb-3 tw-text-[12px] tw-font-semibold tw-text-text-soft">OR</span>

											<div className="tw-min-w-[220px] tw-flex-1 tw-space-y-1.5">
												<Label>Group</Label>
												<ComboSearch
													value={values.group_id}
													onChange={(value: string) => {
														setFieldValue("group_id", value);
														setFieldValue("customer_id", "");
													}}
													placeholder="Search group..."
													className="tw-h-11 tw-w-full tw-justify-between tw-rounded-[10px] tw-border-border tw-text-[14px]"
													optionRenderer={({ name, description }: { name?: string; description?: string }) => (
														<div className="tw-flex tw-items-center tw-gap-2">
															<p>{name}</p>
															{description && <span className="tw-text-[11px] tw-text-text-soft">{description}</span>}
														</div>
													)}
													queryKey="group"
													queryFN={getInvoiceGroups}
													mapFN={(option) => ({
														label: `${option.name}`,
														value: String(option.id ?? ""),
														info: option,
													})}
												/>
											</div>

											<CustomModal
												title={`Add ${customerLabel}`}
												trigger={
													<Button type="button" size="sm" variant="outline" className="tw-gap-1.5">
														<Plus className="tw-h-4 tw-w-4" />
														New {customerLabel}
													</Button>
												}
											>
												<NewCustomerModal
													onCreate={(customer) => {
														setFieldValue("customer", customer ?? null);
														setFieldValue("customer_id", String(customer?.id ?? ""));
													}}
												/>
											</CustomModal>
										</div>
									)}

									<div className="tw-w-full tw-space-y-1.5 sm:tw-w-[200px]">
										<Label htmlFor="due_date">Due date</Label>
										<Input id="due_date" name="due_date" type="date" value={values.due_date} onChange={handleChange} />
									</div>
								</div>
							</section>

							{/* Items */}
							<section className="tw-rounded-lg tw-border tw-border-border tw-bg-white tw-p-4 tw-shadow-card sm:tw-p-5">
								<h2 className="tw-mb-3 tw-text-[15px] tw-font-bold tw-text-text">{invoiceLabel} Items</h2>

								<FieldArray name="invoice_items">
									{({ push, replace, remove }) => (
										<>
											<div className="tw-mb-4 tw-flex tw-flex-wrap tw-items-end tw-gap-3">
												<div className="tw-min-w-[240px] tw-flex-1 tw-space-y-1.5">
													<Label>Select items from inventory</Label>
													<ComboSearch
														onChange={(value: string, option: ProductOption) => {
															if ((option.available_qty ?? 0) < 1) {
																showErrorToast("No quantity in stock");
																return;
															}

															const existingIndex = values.invoice_items.findIndex(
																({ product_id }) => product_id === value
															);

															if (existingIndex >= 0) {
																const existing = values.invoice_items[existingIndex];
																replace(existingIndex, { ...existing, qty: existing.qty + 1 });
															} else {
																push({ product_id: value, qty: 1, product: option });
															}
														}}
														placeholder="Search product..."
														className="tw-h-11 tw-w-full tw-justify-between tw-rounded-[10px] tw-border-border tw-text-[14px]"
														optionRenderer={({ name, available_qty, sku }: ProductOption) => (
															<div>
																<p>
																	{name} (SKU: {sku})
																</p>
																<p className="tw-text-[11px] tw-text-text-soft">
																	{renderNumber(available_qty ?? 0)} remaining
																</p>
															</div>
														)}
														queryKey="items"
														queryFN={getInventory}
														mapFN={(option: ProductOption) => ({
															label: `${option.name} (${option.available_qty})`,
															value: String(option.id),
															info: option,
														})}
													/>
												</div>

												<CustomModal
													title="Add Product"
													trigger={
														<Button type="button" size="sm" variant="outline" className="tw-gap-1.5">
															<Plus className="tw-h-4 tw-w-4" />
															New Product
														</Button>
													}
												>
													<AddProductModal
														onSubmit={(product) => {
															if (!product) return;

															const existingIndex = values.invoice_items.findIndex(
																({ product_id }) => product_id === String(product.id)
															);

															if (existingIndex >= 0) {
																const existing = values.invoice_items[existingIndex];
																replace(existingIndex, { ...existing, qty: existing.qty + 1 });
															} else {
																push({ product_id: String(product.id), qty: 1, product });
															}
														}}
													/>
												</CustomModal>
											</div>

											{values.invoice_items.length === 0 ? (
												<div className="tw-rounded-[10px] tw-border tw-border-dashed tw-border-border">
													<EmptyState
														title={`No items added to this ${invoiceLabel.toLowerCase()}`}
														description="Search your inventory above to add the first item."
													/>
												</div>
											) : (
												<div className="tw-space-y-2">
													{values.invoice_items.map((item, index) => (
														<div
															key={item.product_id}
															className="tw-grid tw-items-center tw-gap-3 tw-rounded-[10px] tw-border tw-border-border tw-p-3 sm:tw-grid-cols-[1fr,120px,110px,120px,44px]"
														>
															<div className="tw-min-w-0">
																<p className="tw-truncate tw-text-[14px] tw-font-medium tw-text-text">
																	{item.product.name}
																</p>
																<p className="tw-text-[12px] tw-text-text-soft">SKU: {item.product.sku ?? "—"}</p>
															</div>

															<div className="tw-text-[13.5px] tw-text-text">
																<span className="tw-text-[12px] tw-text-text-soft sm:tw-hidden">Price: </span>
																{renderCurrency(item.product.price ?? 0)}
															</div>

															<div>
																<Input
																	type="number"
																	min={1}
																	aria-label={`Quantity for ${item.product.name}`}
																	value={item.qty}
																	className="tw-h-9"
																	onChange={(event) => {
																		const qty = event.target.valueAsNumber;
																		if (qty > (item.product.available_qty ?? 0)) {
																			showErrorToast("Not enough inventory in stock");
																			return;
																		}
																		setFieldValue(`invoice_items.${index}.qty`, qty);
																	}}
																/>
															</div>

															<div className="tw-text-[14px] tw-font-semibold tw-text-text">
																{renderCurrency(Number(item.product.price ?? 0) * item.qty)}
															</div>

															<button
																type="button"
																aria-label={`Remove ${item.product.name}`}
																onClick={() => remove(index)}
																className="tw-justify-self-start tw-rounded-lg tw-p-2 tw-text-red tw-transition-colors hover:tw-bg-red-tint sm:tw-justify-self-center"
															>
																<X className="tw-h-4 tw-w-4" />
															</button>
														</div>
													))}
												</div>
											)}

											{typeof errors.invoice_items === "string" && touched.invoice_items && (
												<p className="tw-mt-2 tw-text-[12.5px] tw-text-red">{errors.invoice_items}</p>
											)}
										</>
									)}
								</FieldArray>
							</section>

							{/* Totals + payment */}
							<div className="tw-grid tw-gap-4 lg:tw-grid-cols-2">
								<section className="tw-rounded-lg tw-border tw-border-border tw-bg-white tw-p-4 tw-shadow-card sm:tw-p-5">
									<h2 className="tw-mb-3 tw-text-[15px] tw-font-bold tw-text-text">Payment Method</h2>
									<div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
										{paymentMethods.map(({ value, label, icon: Icon }) => (
											<label
												key={value}
												htmlFor={value}
												className={cn(
													"tw-flex tw-cursor-pointer tw-items-center tw-gap-2.5 tw-rounded-[10px] tw-border tw-p-3.5 tw-text-[14px] tw-font-medium tw-transition-colors",
													values.payment_method === value
														? "tw-border-teal tw-bg-teal-tint tw-text-teal-dark"
														: "tw-border-border tw-text-text hover:tw-border-teal/40"
												)}
											>
												<input
													type="radio"
													name="payment_method"
													id={value}
													value={value}
													checked={values.payment_method === value}
													onChange={handleChange}
													className="tw-sr-only"
												/>
												<Icon className="tw-h-5 tw-w-5" />
												{label}
											</label>
										))}
									</div>
									{touched.payment_method && errors.payment_method && (
										<p className="tw-mt-2 tw-text-[12.5px] tw-text-red">{errors.payment_method}</p>
									)}
								</section>

								<section className="tw-rounded-lg tw-border tw-border-border tw-bg-white tw-p-4 tw-shadow-card sm:tw-p-5">
									<h2 className="tw-mb-3 tw-text-[15px] tw-font-bold tw-text-text">Summary</h2>

									<div className="tw-flex tw-items-center tw-justify-between tw-py-1.5">
										<span className="tw-text-[13px] tw-text-text-soft">Subtotal</span>
										<span className="tw-text-[13.5px] tw-text-text">{renderCurrency(subTotal)}</span>
									</div>

									<div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-py-1.5">
										<Label htmlFor="discount" className="tw-text-[13px] tw-font-normal tw-text-text-soft">
											Discount (%)
										</Label>
										<Input
											id="discount"
											name="discount"
											type="number"
											min={0}
											max={100}
											value={values.discount}
											onChange={handleChange}
											className="tw-h-9 tw-w-[110px] tw-text-right"
										/>
									</div>

									<div className="tw-flex tw-items-center tw-justify-between tw-py-1.5">
										<span className="tw-text-[13px] tw-text-text-soft">VAT</span>
										<span className="tw-text-[13.5px] tw-text-text">{renderCurrency(vatAmount)}</span>
									</div>

									<div className="tw-my-2 tw-h-px tw-bg-border" />

									<div className="tw-flex tw-items-center tw-justify-between tw-py-1">
										<span className="tw-text-[14px] tw-font-semibold tw-text-text">Grand Total</span>
										<span className="tw-text-[20px] tw-font-bold tw-text-text">{renderCurrency(grandTotal)}</span>
									</div>

									<Button
										type="submit"
										disabled={isLoading}
										className="tw-mt-4 tw-w-full tw-bg-teal tw-text-white hover:tw-bg-teal-dark"
									>
										{isLoading ? "Saving..." : "Save & Send"}
									</Button>
								</section>
							</div>
						</form>
					);
				}}
			</Formik>
		</div>
	);
};

export default AddInvoice;
